import { Link, router } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { formatCurrency, formatDate } from '@/lib/utils'
import { ShoppingCart } from 'lucide-react'
import { BackofficeOrderSummary, BackofficeOrderState } from '@/types'

const FILTERS: { value: '' | BackofficeOrderState; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'ready', label: 'Ready' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
]

const STATE_BADGES: Record<BackofficeOrderState, string> = {
  unpaid: 'bg-secondary text-secondary-foreground',
  refunded: 'bg-secondary text-secondary-foreground',
  ready: 'bg-yellow-600 text-white',
  in_progress: 'bg-blue-600 text-white',
  completed: 'bg-green-600 text-white',
}

type Props = {
  orders: BackofficeOrderSummary[]
  search: string
  filter: string
  pagination: PaginationMeta
}

export default function BackofficeOrdersIndex({ orders, search, filter, pagination }: Props) {
  const setFilter = (value: string) => {
    router.get(window.location.pathname, { search: search || undefined, filter: value || undefined }, {
      preserveState: true,
      preserveScroll: true,
      replace: true,
    })
  }

  return (
    <BackofficeLayout
      title="Orders"
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Orders' },
      ]}
    >
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Orders</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} total</p>
      </div>

      <SearchInput value={search} placeholder="Search by business, account, or order id..." />

      <div className="mt-4 flex items-center gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            className={cn(
              'rounded-sm px-2.5 py-1 text-xs font-medium transition-colors border',
              filter === f.value
                ? 'bg-muted text-foreground border-border/60'
                : 'text-muted-foreground border-transparent hover:bg-muted/60'
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {orders.length === 0 ? (
          <div className="border rounded-lg py-16 text-center">
            <ShoppingCart className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
            <h3 className="text-sm font-medium text-foreground mb-1">No orders found</h3>
            <p className="text-sm text-muted-foreground">Submitted orders will appear here.</p>
          </div>
        ) : (
          <div className="border rounded-lg">
            {orders.map((order, i) => (
              <Link
                key={order.id}
                href={`/backoffice/orders/${order.id}`}
                className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${i > 0 ? 'border-t' : ''}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <ShoppingCart className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground truncate">{order.business_name}</span>
                      <Badge className={cn('font-normal text-xs', STATE_BADGES[order.state])}>{order.state.replace('_', ' ')}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {order.account_name} · {order.currency} {formatCurrency(order.total_cents)}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground shrink-0 ml-4">
                  {formatDate(order.created_at)}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
